/* =========================================================================
   Phrase Studios — The Circle (loyalty), points only — NO tiers.
   Hydrates member-aware UI from window.__phraseLoyalty (set in theme.liquid):
     { signedIn, points, pointsPending, walletPassUrl, earnRatePer1Eur }
   Points source: Joy by Avada. On Joy's FREE plan there is no SDK / customer
   metafield (canUseJoySDK:false), so L.points is not a live balance and we must
   NOT print a fake 0 — the real balance lives in Joy's own widget. Where a
   balance would show, defer to Joy's panel (window.avadaJoyTrigger). The earn
   pills are calculated marketing values and stay regardless.
   ========================================================================= */
(function () {
  'use strict';

  // Hide the Joy by Avada floating "Reward" launcher pill (brand direction).
  // Inline !important beats Joy's own styles regardless of injection order; the
  // observer re-hides it if Joy renders it late. Rewards still open from the
  // account page via window.avadaJoyTrigger.
  function hideThirdPartyBlockers() {
    var el = document.getElementById('Avada-Joy_FloatingButton');
    if (el) el.style.setProperty('display', 'none', 'important');
    // Stopgap: the Klaviyo "10% off" popup renders a full-screen overlay that
    // covers the header and blocks the mobile menu. Force-hide it (inline
    // !important beats Klaviyo's own styles). Re-enable as a Flyout in Klaviyo.
    document.querySelectorAll('div[class*="kl-private-reset-css"]').forEach(function (k) {
      k.style.setProperty('display', 'none', 'important');
    });
    // Klaviyo also adds 'klaviyo-prevent-body-scrolling' to <body> (overflow:hidden) when it
    // shows the popup. Because we hide the popup, that lock can never be dismissed and freezes
    // scrolling on EVERY page. Strip it. (Remove with this whole stopgap once the Klaviyo form
    // is reconfigured as a non-blocking Flyout.)
    if (document.body) document.body.classList.remove('klaviyo-prevent-body-scrolling');
  }
  hideThirdPartyBlockers();
  if ('MutationObserver' in window) {
    var blockMo = new MutationObserver(hideThirdPartyBlockers);
    blockMo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { try { blockMo.disconnect(); } catch (e) {} }, 30000);
  } else {
    var jn = 0, jt = setInterval(function () { hideThirdPartyBlockers(); if (++jn > 60) clearInterval(jt); }, 500);
  }

  // Persistent guard: Klaviyo can show the (hidden) popup on exit-intent / scroll / delay long
  // after load, re-adding the scroll-lock class. Strip it whenever it appears so scroll never
  // freezes. Cheap — only fires on <body> class changes, and leaves other classes untouched.
  if ('MutationObserver' in window && document.body) {
    var scrollLockMo = new MutationObserver(function () {
      if (document.body.classList.contains('klaviyo-prevent-body-scrolling')) {
        document.body.classList.remove('klaviyo-prevent-body-scrolling');
      }
    });
    scrollLockMo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  // iOS bfcache reset: when a page is restored from back/forward cache, any body scroll-lock
  // that was active at navigation time (inline overflow, or an overflow-hidden* / *-open /
  // klaviyo class) is restored verbatim and frozen. The theme has no other pageshow handler —
  // clear every known lock so a restored page is always scrollable.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.body.style.overflow = '';
    document.body.classList.remove(
      'overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet',
      'overflow-hidden-desktop', 'phrase-filter-sheet-open', 'klaviyo-prevent-body-scrolling'
    );
  });

  const L = window.__phraseLoyalty;
  if (!L) return;

  function earnRate() {
    return Number(L.earnRatePer1Eur) || 10; // 10 PC per €1 default
  }
  function fmtPoints(n) {
    return (n || 0).toLocaleString('en-GB');
  }
  function earnForPriceCents(cents) {
    return Math.floor((cents / 100) * earnRate());
  }
  // L.points is only authoritative if Joy ever populates it (>0); otherwise the
  // real balance is inside Joy's widget and we defer to it.
  function hasRealPoints() {
    return Number(L.points) > 0;
  }
  function joyAvailable() {
    return typeof window.avadaJoyTrigger === 'function';
  }
  function openJoy() {
    if (joyAvailable()) {
      try { window.avadaJoyTrigger(); } catch (e) { /* noop */ }
    }
  }
  function makeJoyLauncher(el, label) {
    el.textContent = label;
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.style.cursor = 'pointer';
    el.addEventListener('click', openJoy);
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openJoy(); }
    });
  }

  // Expose for inline use
  window.PhraseLoyalty = { state: L, earnForPriceCents, earnRate };

  // Earn pills on product cards + PDP
  function hydrateEarnPills() {
    document.querySelectorAll('[data-phrase-flc-earn]').forEach((el) => {
      const cents = parseInt(el.getAttribute('data-price-cents'), 10);
      if (!isFinite(cents) || cents <= 0) return;
      const earn = earnForPriceCents(cents);
      if (earn < 1) return;
      const valueEl = el.querySelector('[data-phrase-flc-earn-value]');
      if (valueEl) valueEl.textContent = earn;
      else el.textContent = earn + ' PC';
    });
  }

  // Member balance pill (PDP, signed in) — only show a real number, never 0.
  function hydrateBalancePill() {
    if (!L.signedIn || !hasRealPoints()) return;
    document.querySelectorAll('[data-phrase-flc-balance]').forEach((el) => {
      el.textContent = fmtPoints(L.points);
      el.hidden = false;
    });
    document.querySelectorAll('[data-phrase-flc-balance-line]').forEach((el) => {
      el.hidden = false;
    });
  }

  // Account-page membership block — show the real balance, else defer to Joy.
  function hydrateMembershipBlock() {
    const block = document.querySelector('[data-phrase-membership]');
    if (!block) return;
    if (!L.signedIn) { block.hidden = true; return; }
    const balanceBlock = block.querySelector('.phrase-membership__balance-block');
    const balanceEl = block.querySelector('[data-phrase-membership-balance]');
    const pendingEl = block.querySelector('[data-phrase-membership-pending]');
    if (hasRealPoints()) {
      if (balanceEl) balanceEl.textContent = fmtPoints(L.points);
      if (pendingEl) {
        if (L.pointsPending > 0) {
          pendingEl.textContent = '+ ' + fmtPoints(L.pointsPending) + ' pending';
          pendingEl.hidden = false;
        } else {
          pendingEl.hidden = true;
        }
      }
    } else if (balanceBlock) {
      // No theme-readable balance (Joy free plan) — hide the "0 PC" block; the
      // "View my rewards" button (Joy panel) is the real points surface.
      balanceBlock.hidden = true;
    }
  }

  // The Circle page — logged-in balance, else a launcher into Joy's panel.
  function hydrateCirclePage() {
    const wrap = document.querySelector('[data-phrase-circle-signed-in]');
    if (!wrap) return;
    if (!L.signedIn) { wrap.hidden = true; return; }
    wrap.hidden = false;
    const balanceEl = wrap.querySelector('[data-phrase-circle-balance]');
    if (!balanceEl) return;
    if (hasRealPoints()) {
      balanceEl.textContent = fmtPoints(L.points) + ' PC';
    } else if (joyAvailable()) {
      makeJoyLauncher(balanceEl, 'View your points');
    } else {
      balanceEl.textContent = 'Member';
    }
  }

  // Loyalty banner — member-aware CTA + heading (no fake balance, no tiers)
  function hydrateLoyaltyBanner() {
    if (!L.signedIn) return;
    document.querySelectorAll('[data-phrase-loyalty-banner-cta]').forEach((el) => {
      if (joyAvailable() && !hasRealPoints()) {
        el.textContent = 'View your points';
        el.setAttribute('href', 'javascript:void(0)');
        el.addEventListener('click', function (e) { e.preventDefault(); openJoy(); });
      } else {
        el.textContent = 'View your membership';
        el.setAttribute('href', '/account');
      }
    });
    document.querySelectorAll('[data-phrase-loyalty-banner-heading]').forEach((el) => {
      el.textContent = hasRealPoints()
        ? 'You have ' + fmtPoints(L.points) + ' Pocket Change.'
        : "You're part of The Circle.";
    });
  }

  function run() {
    hydrateEarnPills();
    hydrateBalancePill();
    hydrateMembershipBlock();
    hydrateCirclePage();
    hydrateLoyaltyBanner();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
